/**
 * System - Singleton Publisher for device and browser information
 *
 * SINGLETON + PUBLISHER: Detects and publishes system information (device type,
 * browser, platform, breakpoints). Publishes updates on viewport resize.
 *
 * PUBLIC API NOTE: Public methods auto-inject caller context as first argument.
 * JSDoc @param lists show only user-provided parameters (what you actually pass).
 *
 * FEATURES:
 * - Device detection (mobile, tablet, desktop)
 * - Browser detection (Chrome, Safari, iOS, Android)
 * - Breakpoint tracking (mobile vs desktop)
 * - Native app detection
 * - Viewport-based updates
 *
 * @class System
 * @extends Publisher
 * @singleton
 */
define("System", [`Publisher`, `module`], (Super, Self) => {
	return Super.inherit({
		namespace: Self.id,
		singleton: true,
		requires: [`DOM`, `mobile-detect`],
		constructor() {
			const { DOM } = this;
			const { navigator } = window;
			const { userAgent } = navigator;
			const MobileDetect = this.require(`mobile-detect`);
			const mobileDetect = new MobileDetect(userAgent); // instantiate the mobile-detect.js library - https://hgoebl.github.io/mobile-detect.js
			const mobilePlatform = mobileDetect.mobile();

			this.update({
				breakpoint: null, // desktop | mobile
				isAndroid: mobileDetect.is(`AndroidOS`),
				isBrowser: true, // potentially overridden later,
				isChrome: mobileDetect.is(`Chrome`),
				isGooglePaySupported: false, // potentially overridden later
				isiOS: mobileDetect.is(`iOS`),
				isMobile: mobilePlatform !== null,
				isNativeApp: false, // potentially overridden later
				isSafari: mobileDetect.is(`Safari`),
				allowAnalyticsCookies: null, // null = not set, will use geo-based defaults
				allowFunctionalCookies: null, // null = not set, will use geo-based defaults
				allowMarketingCookies: null, // null = not set, will use geo-based defaults
				platform: `web`,
			});

			// update any properties based on viewport dimensions
			this.checkViewport();

			// trigger the handler on viewport resize
			DOM.on({
				event: `resize`,
				handler: this.checkViewport,
				matchContainer: false,
				passive: true,
				target: window,
			});
		},

		api: {
			/**
			 * Set app mode (web, iOS, Android).
			 *
			 * @method System#setMode
			 * @param {Object} options - Mode options
			 * @param {string} options.mode - App mode: 'web', 'ios', or 'android'
			 * @param {boolean} [options.googlePay=false] - Google Pay support
			 */
			setMode: {
				public: true,
				value(context, options) {
					const {
						platform,
						mode, // default | ios | android
						googlePay: isGooglePaySupported = false,
						allowAnalyticsCookies = null,
						allowFunctionalCookies = null,
						allowMarketingCookies = null,
					} = options;

					const digestUpdate = { platform };

					if (mode === `web`) {
						// default, browser or embedded in a webview
						this.assign(digestUpdate, {
							isBrowser: true,
							isNativeApp: false,
							isGooglePaySupported,
							allowAnalyticsCookies,
							allowFunctionalCookies,
							allowMarketingCookies,
						});
					} else if (mode === `ios`) {
						// native iOS app
						this.assign(digestUpdate, {
							isAndroid: false,
							isNativeApp: true,
							isBrowser: false,
							isiOS: true,
							isGooglePaySupported,
							allowAnalyticsCookies,
							allowFunctionalCookies,
							allowMarketingCookies,
						});
					} else if (mode === `android`) {
						// native android app
						this.assign(digestUpdate, {
							isAndroid: true,
							isNativeApp: true,
							isBrowser: false,
							isiOS: false,
							isGooglePaySupported,
							allowAnalyticsCookies,
							allowFunctionalCookies,
							allowMarketingCookies,
						});
					} else {
						throw new Error(`Unrecognized mode: ${mode}`);
					}

					this.update(digestUpdate);
				},
			},

			checkViewport: {
				throttle: 100, // don't fire too often
				value() {
					const { DOM } = this;
					const viewportDimensions = DOM.getViewportDimensions();
					const { width: viewportWidth } = viewportDimensions;
					const breakpoint = viewportWidth < 800 ? `mobile` : `desktop`;

					this.update({ breakpoint });
				},
			},
		},
	});
});

//# sourceMappingURL=System.js.map